import { ReactNode } from "react";
import { Checkbox, FormControlLabel, MenuItem, TextField } from "@mui/material";
import { Location, SiteStatus } from "../../../interfaces/interfaces";

export interface LocationFormValues {
  name: string;
  locationPin: string;
  address: string;
  postcode: string;
  latitude: string;
  longitude: string;
  email: string;
  phone: string;
  layoutLink: string;
  layoutId: string;
  apiId: string;
  nitroCmmsLocationId: string;
  reviewlink: string;
  open: string;
  close: string;
  hasPawwash: boolean;
  is24: boolean;
  siteStatus: SiteStatus;
  alerts: boolean;
  dry: string;
  pet8kg: string;
  pet14kg: string;
  petdry: string;
  wash8kg: string;
  wash14kg: string;
  wash18kg: string;
}

export const EMPTY_LOCATION_FORM: LocationFormValues = {
  name: "",
  locationPin: "",
  address: "",
  postcode: "",
  latitude: "",
  longitude: "",
  email: "",
  phone: "",
  layoutLink: "",
  layoutId: "",
  apiId: "",
  nitroCmmsLocationId: "",
  reviewlink: "",
  open: "",
  close: "",
  hasPawwash: false,
  is24: false,
  siteStatus: "active",
  alerts: false,
  dry: "",
  pet8kg: "",
  pet14kg: "",
  petdry: "",
  wash8kg: "",
  wash14kg: "",
  wash18kg: "",
};

export function locationToForm(location: Location): LocationFormValues {
  return {
    name: location.name || "",
    locationPin: location.locationPin || "",
    address: location.address || "",
    postcode: location.post_code || "",
    latitude: location.latitude?.toString() || "",
    longitude: location.longitude?.toString() || "",
    email: location.email || "",
    phone: location.phone || "",
    layoutLink: location.layout_link || "",
    layoutId: location.layout_site_id?.toString() || "",
    apiId: location.location_api_id || "",
    nitroCmmsLocationId:
      location.nitro_cmms_location_id != null ? String(location.nitro_cmms_location_id) : "",
    reviewlink: location.google_review_link || "",
    open: location.open_time || "",
    close: location.close_time || "",
    hasPawwash: location.has_paw_wash || false,
    is24: location.is_24 || false,
    siteStatus:
      location.site_status || (location.is_coming_soon ? "coming_soon" : "active"),
    alerts: location.alerts_enabled || false,
    dry: location.dry_15_mint_price || "",
    pet8kg: location.pet_8kg_price || "",
    pet14kg: location.pet_14kg_price || "",
    petdry: location.pet_15kg_dry_price || "",
    wash8kg: location.wash_8kg_price || "",
    wash14kg: location.wash_14kg_price || "",
    wash18kg: location.wash_18kg_price || "",
  };
}

type TextKey = {
  [K in keyof LocationFormValues]: LocationFormValues[K] extends string ? K : never;
}[keyof LocationFormValues];

type FieldProps = {
  name: TextKey;
  label: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
  /** Key the API reports validation errors against, when it differs from `name`. */
  errorKey?: string;
  transform?: (value: string) => string;
};

type Props = {
  values: LocationFormValues;
  onChange: (patch: Partial<LocationFormValues>) => void;
  errors: Record<string, string>;
  disabled?: boolean;
  /** Adding a location needs every field; editing allows integrations to be blank. */
  mode: "add" | "edit";
  /** Rendered under the integration fields (the Sync machines control). */
  integrationExtra?: ReactNode;
};

export default function LocationFormFields({
  values,
  onChange,
  errors,
  disabled,
  mode,
  integrationExtra,
}: Props) {
  const adding = mode === "add";

  const field = ({
    name,
    label,
    type,
    required,
    placeholder,
    helperText,
    errorKey,
    transform,
  }: FieldProps) => {
    const message = errors[errorKey ?? name];
    return (
      <TextField
        key={name}
        name={name}
        label={label}
        type={type ?? "text"}
        required={required}
        placeholder={placeholder}
        value={values[name]}
        onChange={(e) =>
          onChange({
            [name]: transform ? transform(e.target.value) : e.target.value,
          } as Partial<LocationFormValues>)
        }
        error={!!message}
        helperText={message || helperText}
        disabled={disabled}
        fullWidth
        slotProps={
          type === "time"
            ? { inputLabel: { shrink: true } }
            : type === "number"
              ? { htmlInput: { inputMode: "decimal", step: "any", min: name === "latitude" || name === "longitude" ? undefined : 0 } }
              : undefined
        }
      />
    );
  };

  const toggle = (key: "hasPawwash" | "is24" | "alerts", label: string) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={values[key]}
          onChange={(e) => onChange({ [key]: e.target.checked } as Partial<LocationFormValues>)}
          disabled={disabled}
        />
      }
      label={label}
    />
  );

  return (
    <>
      <section className="sq-form-section">
        <h3>Store details</h3>
        <div className="sq-form-grid">
          {field({ name: "name", label: "Store name", required: true })}
          {field({ name: "locationPin", label: "Location pin", required: true, placeholder: "SQGS01" })}
          {field({ name: "address", label: "Address", required: true })}
          {field({ name: "postcode", label: "Postcode", required: true })}
          {field({ name: "latitude", label: "Latitude", type: "number", required: true })}
          {field({ name: "longitude", label: "Longitude", type: "number", required: true })}
        </div>
      </section>

      <section className="sq-form-section">
        <h3>Contact</h3>
        <div className="sq-form-grid">
          {field({ name: "email", label: "Email", type: "email", required: true })}
          {field({ name: "phone", label: "Phone", type: "tel", required: true })}
        </div>
      </section>

      <section className="sq-form-section">
        <h3>Opening hours</h3>
        <div className="sq-form-grid">
          {field({ name: "open", label: "Open time", type: "time", required: adding })}
          {field({ name: "close", label: "Close time", type: "time", required: adding })}
          <div className="is-full">{toggle("is24", "Open 24 hours")}</div>
        </div>
      </section>

      <section className="sq-form-section">
        <h3>Integrations</h3>
        <div className="sq-form-grid">
          {field({
            name: "layoutLink",
            label: "Layout link",
            type: "url",
            required: true,
            placeholder: "https://www.sqinsights.com",
          })}
          {field({ name: "layoutId", label: "Layout site ID", type: "number", required: adding })}
          {field({ name: "apiId", label: "API ID", required: adding, placeholder: "loc_12345" })}
          {field({
            name: "nitroCmmsLocationId",
            label: "Nitro CMMS location ID",
            errorKey: "nitro_cmms_location_id",
            helperText: "Optional. Integer ID in Nitro for work orders.",
            transform: (v) => v.replace(/\D/g, ""),
          })}
          {field({
            name: "reviewlink",
            label: "Google review link",
            type: "url",
            required: adding,
            placeholder: "https://www.google.com",
          })}
        </div>
        {integrationExtra}
      </section>

      <section className="sq-form-section">
        <h3>Status and features</h3>
        <div className="sq-form-grid">
          <TextField
            select
            label="Site status"
            value={values.siteStatus}
            onChange={(e) => onChange({ siteStatus: e.target.value as SiteStatus })}
            disabled={disabled}
            fullWidth
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="coming_soon">Coming soon</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </TextField>
          <div className="is-full">
            {toggle("alerts", "Alerts enabled")}
            {toggle("hasPawwash", "Has Paw Wash")}
          </div>
        </div>
      </section>

      <section className="sq-form-section">
        <h3>Wash pricing</h3>
        <div className="sq-form-grid is-thirds">
          {field({ name: "wash8kg", label: "8kg", type: "number", required: adding })}
          {field({ name: "wash14kg", label: "14kg", type: "number", required: adding })}
          {field({ name: "wash18kg", label: "18kg", type: "number", required: adding })}
        </div>
      </section>

      <section className="sq-form-section">
        <h3>Dry pricing</h3>
        <div className="sq-form-grid is-thirds">
          {field({ name: "dry", label: "15 minutes", type: "number", required: adding })}
        </div>
      </section>

      <section className="sq-form-section">
        <h3>Pet pricing</h3>
        <div className="sq-form-grid is-thirds">
          {field({ name: "pet8kg", label: "8kg", type: "number", required: adding })}
          {field({ name: "pet14kg", label: "14kg", type: "number", required: adding })}
          {field({ name: "petdry", label: "15kg dry", type: "number", required: adding })}
        </div>
      </section>
    </>
  );
}
